import { useEffect, useMemo, useState } from "react";
import { Download, GraduationCap, Loader2, Mail, Minus, Pencil, Plus, Trash2, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const blankRun = { search_area: "", p: "", n: "", tp: "", fp: "", fn: "", tn: "", ic1: "", ic2: "" };
const blank = {
  dog_id: "", dog_name: "", handler: "", microchip: "", date: "", weather: "", temp: "", wind: "", rain: "",
  runs: Array.from({ length: 10 }, () => ({ ...blankRun })),
  search_systematics: "", odour_recognition: "", distraction_recovery: "", synergy: "",
  obs_systematics: "", obs_odour: "", obs_distraction: "", obs_synergy: "", feedback: "", evaluator_email: "", dog_specialty: "",
};

const BEHAVIOUR = [
  { key: "search_systematics", obs: "obs_systematics", label: "Search Systematics", hint: "Pattern, coverage, pace, flow.", options: ["Poor", "Fair", "Good", "Excellent"] },
  { key: "odour_recognition", obs: "obs_odour", label: "Odour Recognition (COB)", hint: "Change of behaviour clarity.", options: ["Soft", "Moderate", "High", "N/A"] },
  { key: "distraction_recovery", obs: "obs_distraction", label: "Distraction Recovery", hint: "Response to environmental stimuli.", options: ["Slow", "Normal", "Rapid", "N/A"] },
  { key: "synergy", obs: "obs_synergy", label: "Handler-Dog Team Synergy", hint: "Communication, line control, leash pressure.", options: ["Low", "Moderate", "High", "N/A"] },
];

const IC_OPTIONS = [
  ["IC1", "IC1: White - North European"],
  ["IC2", "IC2: White - South European"],
  ["IC3", "IC3: Black"],
  ["IC4", "IC4: Asian (South Asian origin)"],
  ["IC5", "IC5: Chinese, Japanese, Korean or SE Asian"],
  ["IC6", "IC6: Arab, North African or Egyptian"],
];

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

function kpis(runs) {
  const P = runs.reduce((s, r) => s + num(r.p), 0);
  const N = runs.reduce((s, r) => s + num(r.n), 0);
  const TP = runs.reduce((s, r) => s + num(r.tp), 0);
  const FP = runs.reduce((s, r) => s + num(r.fp), 0);
  const FN = runs.reduce((s, r) => s + num(r.fn), 0);
  const TN = runs.reduce((s, r) => s + num(r.tn), 0);
  const pct = (a, b) => (b ? `${Math.round((a / b) * 1000) / 10}%` : "-");
  return [
    ["Detection / Success Rate", pct(TP, P)],
    ["False Negative (Miss) Rate", pct(FN, P)],
    ["False Positive Rate", pct(FP, N)],
    ["Total Precision", pct(TP, TP + FP)],
    ["Overall Accuracy", pct(TP + TN, TP + TN + FP + FN)],
  ];
}

function TrendChart({ sessions }) {
  const xs = (i) => (sessions.length === 1 ? 50 : (i * 100) / (sessions.length - 1));
  const ys = (v) => 5 + (80 * (100 - v)) / 100;
  const shortDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "-");
  const linePath = (key) =>
    sessions
      .map((s, i) => (s[key] == null ? null : { i, v: s[key] }))
      .filter(Boolean)
      .map((p, idx) => `${idx ? "L" : "M"} ${xs(p.i)}, ${ys(p.v)}`)
      .join(" ");
  const edgeAlign = (i) => (i === 0 ? "left-0" : i === sessions.length - 1 ? "right-0" : "");
  const edgeTransform = (i) => (i === 0 ? "" : i === sessions.length - 1 ? "translateX(-100%)" : "translateX(-50%)");
  return (
    <div data-testid="ct-progress-chart" className="pt-1">
      <div className="flex items-center gap-5 mb-2 text-[11px] text-white/50">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-brand-crimson inline-block" /> Detection rate</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-white inline-block" /> Overall accuracy</span>
      </div>
      <div className="relative" style={{ height: 235 }}>
        {[0, 25, 50, 75, 100].map((g) => (
          <div key={g}>
            <div className="absolute left-8 right-0 h-px bg-white/10" style={{ top: `${ys(g)}%` }} />
            <div className="absolute left-0 -translate-y-1/2 text-[10px] text-white/40" style={{ top: `${ys(g)}%` }}>{g}%</div>
          </div>
        ))}
        <svg className="absolute left-8 right-0 bottom-0 top-0 w-[calc(100%-2rem)] h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d={linePath("accuracy")} fill="none" stroke="#e2e6ec" strokeWidth="2" strokeDasharray="6 5" vectorEffect="non-scaling-stroke" />
          <path d={linePath("detection")} fill="none" stroke="#c8102e" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
        </svg>
        {sessions.map((s, i) => (
          <div key={i}>
            {s.accuracy != null && (
              <div className="absolute h-3 w-3 rounded-full bg-white -translate-x-1/2 -translate-y-1/2" style={{ left: `calc(2rem + (100% - 2rem) * ${xs(i) / 100})`, top: `${ys(s.accuracy)}%` }} data-testid={`ct-chart-dot-accuracy-${i}`} />
            )}
            {s.detection != null && (
              <>
                <div className="absolute h-3.5 w-3.5 rounded-full bg-brand-crimson ring-2 ring-white -translate-x-1/2 -translate-y-1/2" style={{ left: `calc(2rem + (100% - 2rem) * ${xs(i) / 100})`, top: `${ys(s.detection)}%` }} data-testid={`ct-chart-dot-detection-${i}`} />
                <div className="absolute -translate-x-1/2 text-[11px] font-bold text-brand-crimson whitespace-nowrap" style={{ left: `calc(2rem + (100% - 2rem) * ${xs(i) / 100})`, top: `calc(${ys(s.detection)}% - 28px)` }} data-testid={`ct-chart-value-${i}`}>
                  {s.detection}%
                </div>
              </>
            )}
            <div className={`absolute bottom-0 text-[10px] text-white/40 whitespace-nowrap ${edgeAlign(i)}`} style={{ left: i === 0 ? "0rem" : i === sessions.length - 1 ? "100%" : `calc(2rem + (100% - 2rem) * ${xs(i) / 100})`, transform: edgeTransform(i) }}>
              S{i + 1} - {shortDate(s.date)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TrainingTab({ requestedDog }) {
  const [records, setRecords] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [mode, setMode] = useState("list");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [dogFilter, setDogFilter] = useState("");
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailAddr, setEmailAddr] = useState("");

  const load = () => {
    api.get("/ct-records").then((r) => setRecords(r.data)).catch(() => setRecords([]));
  };
  useEffect(load, []);
  useEffect(() => {
    api.get("/admin/dogs").then((r) => setDogs(r.data)).catch(() => setDogs([]));
  }, []);

  useEffect(() => {
    if (requestedDog) setDogFilter(requestedDog);
  }, [requestedDog]);

  const openNew = () => {
    setEditing(null);
    setForm(JSON.parse(JSON.stringify(blank)));
    setMode("form");
  };

  const openEdit = (rec) => {
    setEditing(rec);
    const runs = Array.from({ length: 10 }, (_, i) => ({ ...blankRun, ...(rec.runs?.[i] || {}) }));
    setForm({ ...blank, ...rec, runs });
    setMode("form");
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setRun = (i, key, value) => setForm((f) => ({ ...f, runs: f.runs.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)) }));

  const pickDog = (dogId) => {
    const dog = dogs.find((d) => d.id === dogId);
    setForm((f) => ({
      ...f,
      dog_id: dogId || "",
      dog_name: dog ? dog.name : "",
      microchip: dog ? dog.microchip || "" : "",
      dog_specialty: dog?.specialty || "",
    }));
  };

  const live = useMemo(() => kpis(form.runs), [form.runs]);

  const save = async () => {
    if (!form.dog_name.trim() && !form.handler.trim()) {
      toast.error("Pick a dog or enter a handler first");
      return;
    }
    setBusy(true);
    const payload = {
      ...form,
      runs: form.runs.map((r) => ({
        search_area: r.search_area,
        p: num(r.p), n: num(r.n), tp: num(r.tp), fp: num(r.fp), fn: num(r.fn), tn: num(r.tn),
        ic1: r.ic1, ic2: r.ic2,
      })),
    };
    try {
      if (editing) {
        await api.put(`/ct-records/${editing.id}`, payload);
      } else {
        await api.post("/ct-records", payload);
      }
      toast.success("Training record saved");
      setMode("list");
      load();
    } catch (e) {
      toast.error("Could not save the record", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (rec) => {
    try {
      await api.delete(`/ct-records/${rec.id}`);
      setRecords((prev) => prev.filter((r) => r.id !== rec.id));
      toast.success("Record deleted");
    } catch (e) {
      toast.error("Could not delete", { description: formatApiError(e) });
    }
  };

  const downloadPdf = async (rec) => {
    try {
      const res = await api.get(`/ct-records/${rec.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `CT-${rec.dog_name || "record"}-${rec.date || ""}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CT record PDF downloaded");
    } catch (e) {
      toast.error("Could not download the PDF", { description: formatApiError(e) });
    }
  };

  if (records === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="ct-loading-spinner" />
      </div>
    );
  }

  if (mode === "form") {
    return (
      <div data-testid="ct-form" className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-white flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-brand-crimson" /> {editing ? "Edit CT Record" : "New CT Record"}
          </h2>
          <div className="flex gap-2.5">
            <Button variant="outline" onClick={() => setMode("list")} data-testid="ct-cancel" className="border-brand-line text-white/70 hover:text-white hover:bg-white/5 font-eyebrow uppercase tracking-[0.15em] text-[11px] font-bold">
              Cancel
            </Button>
            <Button disabled={busy} onClick={save} data-testid="ct-save" className="bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow uppercase tracking-[0.15em] text-[11px] font-bold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save Record
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-brand-line bg-brand-surface p-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Dog</Label>
              <select data-testid="ct-dog-select" value={form.dog_id} onChange={(e) => pickDog(e.target.value)} className="w-full rounded-md border border-brand-line bg-brand-obsidian px-3 py-2 text-sm text-white">
                <option value="">- pick a dog -</option>
                {dogs.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              {form.dog_specialty && (
                <p className="text-[10px] font-eyebrow uppercase tracking-wider font-bold text-brand-crimson" data-testid="ct-dog-discipline">
                  Trained for: {form.dog_specialty}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Handler</Label>
              <Input data-testid="ct-input-handler" value={form.handler} onChange={(e) => setField("handler", e.target.value)} className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Microchip No</Label>
              <Input data-testid="ct-input-microchip" value={form.microchip} onChange={(e) => setField("microchip", e.target.value)} className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Date</Label>
              <Input data-testid="ct-input-date" type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Weather</Label>
              <Input data-testid="ct-input-weather" value={form.weather} onChange={(e) => setField("weather", e.target.value)} className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Temp</Label>
              <Input data-testid="ct-input-temp" value={form.temp} onChange={(e) => setField("temp", e.target.value)} className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Wind</Label>
              <Input data-testid="ct-input-wind" value={form.wind} onChange={(e) => setField("wind", e.target.value)} className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Rain</Label>
              <Input data-testid="ct-input-rain" value={form.rain} onChange={(e) => setField("rain", e.target.value)} className="bg-brand-obsidian border-brand-line" />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-brand-line bg-brand-surface p-5">
          <p className="font-display font-bold text-white mb-3">1. Raw Run Data (Trial Matrix - 10 runs)</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ct-run-table">
              <thead>
                <tr className="text-left text-[10px] font-eyebrow uppercase tracking-wider text-white/40">
                  <th className="py-2 pr-2">Run</th>
                  <th className="py-2 pr-2">Search area / details</th>
                  <th className="py-2 px-1 text-center" title="Hides placed">P</th>
                  <th className="py-2 px-1 text-center" title="Blank areas">N</th>
                  <th className="py-2 px-1 text-center" title="True positive">TP</th>
                  <th className="py-2 px-1 text-center" title="False positive">FP</th>
                  <th className="py-2 px-1 text-center" title="False negative">FN</th>
                  <th className="py-2 px-1 text-center" title="True negative">TN</th>
                  <th className="py-2 px-2">IC Ref 1</th>
                  <th className="py-2 px-2">IC Ref 2</th>
                </tr>
              </thead>
              <tbody>
                {form.runs.map((run, i) => (
                  <tr key={i} className="border-t border-brand-line/60">
                    <td className="py-1.5 pr-2 text-white/40">{i + 1}</td>
                    <td className="py-1.5 pr-2">
                      <Input data-testid={`ct-run-${i + 1}-area`} value={run.search_area} onChange={(e) => setRun(i, "search_area", e.target.value)} placeholder="e.g. Vehicle park" className="bg-brand-obsidian border-brand-line h-8 text-xs" />
                    </td>
                    {["p", "n", "tp", "fp", "fn", "tn"].map((k) => (
                      <td key={k} className="py-1.5 px-1">
                        <Input data-testid={`ct-run-${i + 1}-${k}`} type="number" min="0" value={run[k]} onChange={(e) => setRun(i, k, e.target.value)} className="bg-brand-obsidian border-brand-line h-8 w-14 text-center text-xs" />
                      </td>
                    ))}
                    {["ic1", "ic2"].map((k) => (
                      <td key={k} className="py-1.5 px-2">
                        <select data-testid={`ct-run-${i + 1}-${k}`} value={run[k]} onChange={(e) => setRun(i, k, e.target.value)} className="w-full rounded-md border border-brand-line bg-brand-obsidian px-2 py-1.5 text-xs text-white">
                          <option value="">-</option>
                          {IC_OPTIONS.map(([v, lab]) => (
                            <option key={v} value={v}>{lab}</option>
                          ))}
                        </select>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-white/40 mt-2">P = hides placed | N = blank areas | TP = true positive | FP = false positive | FN = false negative | TN = true negative. KPIs below calculate automatically.</p>
        </div>

        <div className="rounded-lg border border-brand-line bg-brand-surface p-5">
          <p className="font-display font-bold text-white mb-3">2. Key Performance Indicators</p>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {live.map(([label, val]) => (
              <div key={label} className="rounded-md border border-brand-line bg-brand-obsidian p-3 text-center" data-testid={`ct-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <p className="text-[9px] font-eyebrow uppercase tracking-wider text-white/40 font-bold">{label}</p>
                <p className="text-xl font-display font-bold text-brand-crimson mt-1">{val}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-brand-line bg-brand-surface p-5">
          <p className="font-display font-bold text-white mb-3">3. Search Efficiency &amp; Behaviour Evaluation</p>
          <div className="space-y-4">
            {BEHAVIOUR.map((b) => (
              <div key={b.key} className="rounded-md border border-brand-line bg-brand-obsidian p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-sm font-bold text-white w-full sm:w-auto">{b.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {b.options.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        data-testid={`ct-behaviour-${b.key}-${opt.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        onClick={() => setField(b.key, form[b.key] === opt ? "" : opt)}
                        className={`px-3 py-1.5 rounded-md border text-[11px] font-bold transition-all duration-200 ${form[b.key] === opt ? "border-brand-crimson bg-brand-crimson text-white" : "border-brand-line bg-brand-surface text-white/60 hover:text-white"}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  <span className="text-[11px] text-white/40">{b.hint}</span>
                </div>
                <Input data-testid={`ct-behaviour-obs-${b.key}`} value={form[b.obs]} onChange={(e) => setField(b.obs, e.target.value)} placeholder="Observations / behavioural cues..." className="bg-brand-surface border-brand-line mt-3 h-8 text-xs" />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-brand-line bg-brand-surface p-5">
          <p className="font-display font-bold text-white mb-3">4. Qualitative Feedback &amp; Action Plan</p>
          <Textarea data-testid="ct-feedback" value={form.feedback} onChange={(e) => setField("feedback", e.target.value)} rows={5} placeholder="Key learnings / correction points / environmental factors / progression goals..." className="bg-brand-obsidian border-brand-line" />
          <div className="mt-3 space-y-1.5">
            <Label className="text-white/70 text-xs">Evaluator email (to send this record for sign-off)</Label>
            <Input data-testid="ct-input-evaluator-email" type="email" value={form.evaluator_email} onChange={(e) => setField("evaluator_email", e.target.value)} placeholder="instructor@example.co.uk" className="bg-brand-obsidian border-brand-line" />
          </div>
        </div>
      </div>
    );
  }

  const openEmail = (rec) => {
    setEmailTarget(rec);
    setEmailAddr(rec.evaluator_email || "");
  };

  const sendEmail = async () => {
    if (!emailAddr.trim()) {
      toast.error("Enter the evaluator's email address");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/ct-records/${emailTarget.id}/email`, { email: emailAddr.trim() });
      toast.success(`Record sent to ${emailAddr.trim()}`);
      setEmailTarget(null);
      load();
    } catch (e) {
      toast.error("Could not send the email", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const filtered = dogFilter ? records.filter((r) => r.dog_id === dogFilter) : records;
  const byDate = [...filtered].sort((a, b) => (a.date || a.created_at || "").localeCompare(b.date || b.created_at || ""));
  const sessionsNewestFirst = [...byDate].reverse();
  const trendFor = (i) => {
    const cur = sessionsNewestFirst[i]?.kpis?.detection_rate;
    const older = sessionsNewestFirst[i + 1]?.kpis?.detection_rate;
    if (cur == null || older == null || cur === older) return "same";
    return cur > older ? "up" : "down";
  };
  const dogLabel = dogFilter ? (dogs.find((d) => d.id === dogFilter)?.name || "") : "";

  return (
    <div data-testid="ct-admin-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm text-white/50">
          {records.length} training record{records.length === 1 ? "" : "s"}
        </p>
        <Button onClick={openNew} data-testid="ct-new-record" className="bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-[11px] uppercase tracking-[0.2em] font-bold">
          <Plus className="h-4 w-4" /> New CT Record
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="ct-dog-chips">
        <button
          onClick={() => setDogFilter("")}
          data-testid="ct-chip-all"
          className={`px-3.5 py-1.5 rounded-full border text-xs font-bold transition-all duration-200 ${dogFilter === "" ? "border-brand-crimson bg-brand-crimson text-white" : "border-brand-line bg-brand-surface text-white/60 hover:text-white"}`}
        >
          All dogs
        </button>
        {[...dogs].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((d) => (
          <button
            key={d.id}
            onClick={() => setDogFilter(d.id)}
            data-testid={`ct-chip-${(d.name || "").toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`px-3.5 py-1.5 rounded-full border text-xs font-bold transition-all duration-200 ${dogFilter === d.id ? "border-brand-crimson bg-brand-crimson text-white" : "border-brand-line bg-brand-surface text-white/60 hover:text-white"}`}
          >
            {d.name}
          </button>
        ))}
      </div>

      {records.length === 0 ? (
        <div className="rounded-lg border border-brand-line bg-brand-surface p-16 text-center text-white/50" data-testid="ct-empty">
          No training records yet. Click "New CT Record" to fill in your first Continuation Training sheet.
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-brand-line bg-brand-surface p-16 text-center text-white/50" data-testid="ct-filtered-empty">
          No CT records for this dog yet.
        </div>
      ) : (
        <>
          {dogFilter && filtered.length > 0 && (
            <div className="rounded-lg border border-brand-line bg-brand-surface p-5 mb-6" data-testid="ct-chart-card">
              <p className="font-display font-bold text-white mb-1 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-brand-crimson" /> {dogLabel} - progress over sessions
              </p>
              <p className="text-[11px] text-white/40 mb-2">Detection rate (crimson) and overall accuracy (grey) per training session. Dashed line = accuracy.</p>
              <TrendChart sessions={byDate.map((r) => ({ date: r.date, detection: r.kpis?.detection_rate ?? null, accuracy: r.kpis?.accuracy ?? null }))} />
            </div>
          )}
          {dogFilter && (
            <p className="text-[11px] font-eyebrow uppercase tracking-[0.2em] font-bold text-white/40 mb-4 flex items-center gap-2" data-testid="ct-timeline-label">
              <GraduationCap className="h-3.5 w-3.5 text-brand-crimson" /> {dogLabel} - training timeline (newest first)
            </p>
          )}
          <div className={dogFilter ? "relative space-y-4 pl-6 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-px before:bg-brand-line" : "space-y-3"}>
            {sessionsNewestFirst.map((rec, i) => {
              const k = rec.kpis || {};
              const trend = trendFor(i);
              return (
                <div key={rec.id} className={`flex flex-wrap items-center gap-4 rounded-lg border border-brand-line bg-brand-surface p-4 ${dogFilter ? "relative" : ""}`} data-testid={`ct-record-row-${rec.id}`}>
                  {dogFilter && <span className="absolute -left-[22px] top-1/2 -translate-y-1/2 h-2.5 w-2.5 rounded-full bg-brand-crimson ring-4 ring-brand-crimson/20" data-testid={`ct-timeline-dot-${rec.id}`} />}
                  <div className="h-11 w-11 shrink-0 rounded-md bg-brand-crimson/15 border border-brand-crimson/30 flex items-center justify-center">
                    <GraduationCap className="h-5 w-5 text-brand-crimson" />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-display font-bold text-white">
                      {dogFilter ? `Session ${byDate.length - i}` : rec.dog_name || "Unassigned"} <span className="text-white/40 text-xs font-normal">- {dogFilter ? rec.date || "no date" : rec.handler || "no handler set"}</span>
                    </p>
                    <p className="text-xs text-white/50 mt-0.5 flex flex-wrap items-center gap-2">
                      <span>
                        {dogFilter ? `${rec.handler || "no handler"}` : `${rec.date || "no date"}`} - Detection {k.detection_rate ?? "-"} | Accuracy {k.accuracy ?? "-"}
                      </span>
                      {dogFilter && (
                        <span data-testid={`ct-trend-${rec.id}`} className={`inline-flex items-center gap-1 font-bold ${trend === "up" ? "text-emerald-400" : trend === "down" ? "text-red-400" : "text-white/30"}`} title="Detection rate vs previous session">
                          {trend === "up" ? <TrendingUp className="h-3.5 w-3.5" /> : trend === "down" ? <TrendingDown className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                          {trend === "up" ? "improving" : trend === "down" ? "dipped" : "steady"}
                        </span>
                      )}
                      {rec.emailed_at && <span className="text-white/30">- sent to {rec.emailed_to}</span>}
                      {rec.created_at && (
                        <span className="text-white/30" data-testid={`ct-logged-${rec.id}`}>
                          - logged {new Date(rec.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openEmail(rec)} data-testid={`ct-email-${rec.id}`} className="p-2 rounded-md border border-brand-line text-white/40 hover:text-brand-crimson hover:border-brand-crimson/40 transition-colors" aria-label="Email to instructor" title="Email to instructor">
                      <Mail className="h-4 w-4" />
                    </button>
                    <button onClick={() => downloadPdf(rec)} data-testid={`ct-pdf-${rec.id}`} className="p-2 rounded-md border border-brand-line text-white/40 hover:text-brand-crimson hover:border-brand-crimson/40 transition-colors" aria-label="Download PDF" title="Download PDF">
                      <Download className="h-4 w-4" />
                    </button>
                    <button onClick={() => openEdit(rec)} data-testid={`ct-edit-${rec.id}`} className="p-2 rounded-md border border-brand-line text-white/40 hover:text-white hover:border-white/40 transition-colors" aria-label="Edit record" title="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(rec)} data-testid={`ct-delete-${rec.id}`} className="p-2 rounded-md border border-brand-line text-white/40 hover:text-red-400 hover:border-red-500/40 transition-colors" aria-label="Delete record" title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <Dialog open={!!emailTarget} onOpenChange={(o) => !o && setEmailTarget(null)}>
        <DialogContent className="bg-brand-surface border-brand-line max-w-md" data-testid="ct-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">Email CT record</DialogTitle>
            <DialogDescription className="text-white/50 text-sm">
              The branded PDF record is attached - your evaluator can reply straight back to you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-white/50">
              Send the branded PDF for <span className="text-white font-bold">{emailTarget?.dog_name}</span> ({emailTarget?.date}) to your instructor / evaluator. They can reply straight back.
            </p>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Send to</Label>
              <Input data-testid="ct-email-to" type="email" value={emailAddr} onChange={(e) => setEmailAddr(e.target.value)} placeholder="instructor@example.co.uk" className="bg-brand-obsidian border-brand-line" />
            </div>
            <Button disabled={busy} onClick={sendEmail} data-testid="ct-email-send" className="w-full bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow uppercase tracking-[0.15em] text-[11px] font-bold">
              <Mail className="h-4 w-4" /> Send Record
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
